import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import { Plus, Box, ArrowRightLeft } from "lucide-react";

export default function Stock() {
  const { user } = useAuth();
  const nav = useNavigate();
  const showPrices = user?.role === "admin";
  const canInward = ["admin", "warehouse_manager"].includes(user?.role);
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [whFilter, setWhFilter] = useState("");
  const [bucketFilter, setBucketFilter] = useState("");
  const [err, setErr] = useState("");
  const [showInward, setShowInward] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const load = () => {
    const params = {};
    if (whFilter) params.warehouse_id = whFilter;
    if (bucketFilter) params.bucket = bucketFilter;
    api.get("/stock", { params }).then(r => setStock(r.data)).catch(e=>setErr(formatApiError(e)));
  };
  useEffect(() => { api.get("/warehouses").then(r => setWarehouses(r.data)); api.get("/products").then(r => setProducts(r.data)); }, []);
  useEffect(() => { load(); }, [whFilter, bucketFilter]);

  return (
    <div className="p-4 md:p-8" data-testid="stock-page">
      <PageHeader title="Stock Ledger" subtitle="Available, faulty, repair, scrap and in-transit buckets per warehouse."
        action={canInward ? (
          <div className="flex gap-2">
            <button onClick={()=>setShowInward(s=>!s)} data-testid="inward-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md"><Plus size={14}/> Inward</button>
            <button onClick={()=>setShowTransfer(s=>!s)} data-testid="transfer-btn" className="inline-flex items-center gap-2 bg-[#18181B] hover:bg-[#27272A] text-white font-semibold text-sm py-2 px-4 rounded-md"><ArrowRightLeft size={14}/> Transfer</button>
          </div>
        ) : null}
      />

      {showInward && <InwardForm warehouses={warehouses} products={products} onDone={()=>{ setShowInward(false); load(); }} />}
      {showTransfer && <TransferForm warehouses={warehouses} products={products} onDone={()=>{ setShowTransfer(false); load(); }} />}

      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={whFilter} onChange={e=>setWhFilter(e.target.value)} className="inp max-w-xs" data-testid="stock-warehouse-filter">
          <option value="">All warehouses</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={bucketFilter} onChange={e=>setBucketFilter(e.target.value)} className="inp max-w-xs" data-testid="stock-bucket-filter">
          <option value="">All buckets</option>
          <option value="available">Available</option>
          <option value="faulty">Faulty</option>
          <option value="repair">Repair</option>
          <option value="scrap">Scrap</option>
        </select>
      </div>

      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {stock.length === 0 ? <EmptyState title="No stock entries" icon={Box} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Warehouse</th>
                  <th className="px-4 py-3 text-left">SKU</th>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left">Bucket</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Min</th>
                  {showPrices && <th className="px-4 py-3 text-right">Rate</th>}
                  {showPrices && <th className="px-4 py-3 text-right">Value</th>}
                </tr>
              </thead>
              <tbody>
                {stock.map((s, i) => {
                  const low = s.bucket === "available" && s.quantity <= (s.min_stock||0) && (s.min_stock||0) > 0;
                  return (
                    <tr key={i} onClick={()=>nav(`/stock/detail/${s.warehouse_id}/${s.product_id}`)} data-testid={`stock-row-${s.sku}`} className={`border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer ${low ? "bg-rose-50/50" : ""}`}>
                      <td className="px-4 py-3">{s.warehouse_name}</td>
                      <td className="px-4 py-3 num font-semibold text-[#F97316]">{s.sku}</td>
                      <td className="px-4 py-3">{s.product_name}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700">{s.bucket}</span></td>
                      <td className={`px-4 py-3 num text-right font-bold ${low ? "text-rose-600" : ""}`}>{s.quantity}</td>
                      <td className="px-4 py-3 num text-right text-zinc-500">{s.min_stock}</td>
                      {showPrices && <td className="px-4 py-3 num text-right">₹{s.rate}</td>}
                      {showPrices && <td className="px-4 py-3 num text-right">₹{(s.quantity * (s.rate||0)).toFixed(0)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

function InwardForm({ warehouses, products, onDone }) {
  const [form, setForm] = useState({ warehouse_id:"", source:"Purchase", reference:"", remark:"", items:[{product_id:"", quantity:1, serial_numbers:""}] });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({ product_id: it.product_id, quantity: parseInt(it.quantity)||0,
          serial_numbers: (it.serial_numbers||"").split(",").map(s=>s.trim()).filter(Boolean) })),
      };
      await api.post("/stock/inward", payload);
      onDone();
    } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Card className="p-5 mb-6" data-testid="inward-form">
      <div className="font-display font-semibold mb-3">Inward Stock</div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select required value={form.warehouse_id} onChange={e=>setForm(f=>({...f, warehouse_id:e.target.value}))} className="inp" data-testid="inward-wh">
            <option value="">Warehouse</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <input placeholder="Source" value={form.source} onChange={e=>setForm(f=>({...f, source:e.target.value}))} className="inp" />
          <input placeholder="Reference (PO/Bill #)" value={form.reference} onChange={e=>setForm(f=>({...f, reference:e.target.value}))} className="inp" />
        </div>
        {form.items.map((it,i)=>(
          <div key={i} className="grid grid-cols-12 gap-2">
            <select value={it.product_id} onChange={e=>{const a=[...form.items];a[i]={...a[i],product_id:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-12 md:col-span-5" data-testid={`inward-product-${i}`}>
              <option value="">Product</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty" value={it.quantity} onChange={e=>{const a=[...form.items];a[i]={...a[i],quantity:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-4 md:col-span-2" />
            <input placeholder="Serial #s (comma-sep, for serialized)" value={it.serial_numbers} onChange={e=>{const a=[...form.items];a[i]={...a[i],serial_numbers:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-8 md:col-span-5" />
          </div>
        ))}
        <button type="button" onClick={()=>setForm(f=>({...f, items:[...f.items,{product_id:"",quantity:1,serial_numbers:""}]}))} className="text-xs font-semibold text-[#F97316]">+ Add item</button>
        {err && <div className="text-rose-600 text-sm">{err}</div>}
        <button type="submit" data-testid="inward-save-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Save Inward</button>
      </form>
    </Card>
  );
}

function TransferForm({ warehouses, products, onDone }) {
  const [form, setForm] = useState({ from_warehouse_id:"", to_warehouse_id:"", remark:"", items:[{product_id:"", quantity:1, serial_numbers:""}] });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({ product_id: it.product_id, quantity: parseInt(it.quantity)||0,
          serial_numbers: (it.serial_numbers||"").split(",").map(s=>s.trim()).filter(Boolean) })),
      };
      await api.post("/stock/transfer", payload);
      onDone();
    } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Card className="p-5 mb-6" data-testid="transfer-form">
      <div className="font-display font-semibold mb-3">Warehouse Transfer</div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select required value={form.from_warehouse_id} onChange={e=>setForm(f=>({...f, from_warehouse_id:e.target.value}))} className="inp" data-testid="transfer-from">
            <option value="">From Warehouse</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select required value={form.to_warehouse_id} onChange={e=>setForm(f=>({...f, to_warehouse_id:e.target.value}))} className="inp" data-testid="transfer-to">
            <option value="">To Warehouse</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        {form.items.map((it,i)=>(
          <div key={i} className="grid grid-cols-12 gap-2">
            <select value={it.product_id} onChange={e=>{const a=[...form.items];a[i]={...a[i],product_id:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-12 md:col-span-5" data-testid={`transfer-product-${i}`}>
              <option value="">Product</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty" value={it.quantity} onChange={e=>{const a=[...form.items];a[i]={...a[i],quantity:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-4 md:col-span-2" />
            <input placeholder="Serial #s (if serialized)" value={it.serial_numbers} onChange={e=>{const a=[...form.items];a[i]={...a[i],serial_numbers:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-8 md:col-span-5" />
          </div>
        ))}
        <button type="button" onClick={()=>setForm(f=>({...f, items:[...f.items,{product_id:"",quantity:1,serial_numbers:""}]}))} className="text-xs font-semibold text-[#F97316]">+ Add item</button>
        {err && <div className="text-rose-600 text-sm">{err}</div>}
        <button type="submit" data-testid="transfer-save-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Transfer</button>
      </form>
    </Card>
  );
}
